import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Card, Collapse, Descriptions, Form, Input, Modal, Spin, message } from "antd"
import { FileTextOutlined, PaperClipOutlined, TableOutlined, UserOutlined } from "@ant-design/icons"
import { useNavigate, useParams } from "react-router-dom"
import { TempLayout } from "../../layouts"
import { useSapSupplierStore } from "../../stores/sap-supplier/sapSupplierStore"
import { ISapSupplierPayloadItem } from "../../types"
import SupplierPayloadEditor from "./components/SupplierPayloadEditor"
import { getSupplierEditAccess } from "./utils/supplierEditAccess"
import { getSupplierEditDraft, removeSupplierEditDraft, saveSupplierEditDraft } from "./utils/supplierEditDraft"

const SapSuppliersEdit = () => {
  const { recordId } = useParams()
  const navigate = useNavigate()
  const [commentForm] = Form.useForm()
  const [payload, setPayload] = useState<ISapSupplierPayloadItem[]>([])
  const [initialSnapshot, setInitialSnapshot] = useState<string>("")
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const draftPromptKeyRef = useRef<string | null>(null)
  const { fetchSapSupplierById, currentSupplier, isLoading, error, updateSapSupplier } = useSapSupplierStore()

  const currentEmployeeId = useMemo(() => {
    try {
      const currentSession = JSON.parse(window.localStorage.getItem("session_farms") || "null") as { employee_id?: string } | null
      return currentSession?.employee_id || null
    } catch (_error) {
      return null
    }
  }, [])

  const isApproved = (currentSupplier?.approval_status || "").toUpperCase() === "APPROVED"
  const editAccess = useMemo(
    () => getSupplierEditAccess(currentSupplier?.approval_process_id, currentEmployeeId),
    [currentSupplier?.approval_process_id, currentEmployeeId],
  )
  const isReadOnly = isApproved || !editAccess.canEdit
  const columns = currentSupplier?.columns || []
  const supplierRecordId = currentSupplier?.record_id || null
  const supplierUpdatedAt = currentSupplier?.updated_at || null
  const supplierPayload = useMemo(
    () => (Array.isArray(currentSupplier?.payload) ? currentSupplier.payload : []),
    [currentSupplier?.payload],
  )

  const currentSnapshot = useMemo(() => JSON.stringify({ payload }), [payload])
  const isDirty = hasInitialized && currentSnapshot !== initialSnapshot

  const applyWorkingCopy = useCallback((nextPayload: ISapSupplierPayloadItem[]) => {
    setPayload(nextPayload)
  }, [])

  const fetchSupplier = useCallback(async () => {
    if (recordId) {
      await fetchSapSupplierById(recordId)
    }
  }, [recordId, fetchSapSupplierById])

  useEffect(() => {
    void fetchSupplier()
  }, [fetchSupplier])

  useEffect(() => {
    if (!supplierRecordId) return

    const backendSnapshot = JSON.stringify({ payload: supplierPayload })
    const persistedDraft = getSupplierEditDraft(supplierRecordId)
    const backendUpdatedAt = supplierUpdatedAt
    const conflictKey = `${supplierRecordId}:${persistedDraft?.savedAt || "none"}:${backendUpdatedAt || "none"}`

    applyWorkingCopy(supplierPayload)
    setInitialSnapshot(backendSnapshot)
    setHasInitialized(true)

    if (isReadOnly) {
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (!persistedDraft) {
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (persistedDraft.baseUpdatedAt === backendUpdatedAt) {
      applyWorkingCopy(persistedDraft.payload)
      draftPromptKeyRef.current = conflictKey
      return
    }

    if (draftPromptKeyRef.current === conflictKey) {
      return
    }

    draftPromptKeyRef.current = conflictKey
    Modal.confirm({
      title: "Restore unsaved draft?",
      content: "Ada draft lokal dari versi data sebelumnya. Restore draft untuk lanjut edit, atau discard untuk pakai data terbaru dari server.",
      okText: "Restore Draft",
      cancelText: "Discard Draft",
      onOk: () => applyWorkingCopy(persistedDraft.payload),
      onCancel: () => removeSupplierEditDraft(supplierRecordId),
    })
  }, [supplierRecordId, supplierUpdatedAt, supplierPayload, isReadOnly, applyWorkingCopy])

  useEffect(() => {
    if (!isDirty || isReadOnly) return undefined

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [isDirty, isReadOnly])

  useEffect(() => {
    if (!recordId || !hasInitialized || isReadOnly) return undefined

    if (!isDirty) {
      removeSupplierEditDraft(recordId)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      saveSupplierEditDraft({
        recordId,
        payload,
        savedAt: new Date().toISOString(),
        baseUpdatedAt: supplierUpdatedAt,
      })
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [recordId, payload, isDirty, hasInitialized, isReadOnly, supplierUpdatedAt])

  const handleCancel = () => {
    if (!isDirty) {
      navigate(recordId ? `/sapspl/view/${recordId}` : "/sapspl/history")
      return
    }

    Modal.confirm({
      title: "Discard unsaved changes?",
      content: "Perubahan belum disimpan. Jika keluar sekarang, perubahan akan hilang.",
      okText: "Discard",
      cancelText: "Stay",
      onOk: () => {
        if (recordId) {
          removeSupplierEditDraft(recordId)
        }
        navigate(recordId ? `/sapspl/view/${recordId}` : "/sapspl/history")
      },
    })
  }

  const handleSubmit = async (comment: string) => {
    if (!recordId) {
      void message.error("Record ID is missing")
      return
    }

    if (!payload.length) {
      void message.error("Payload tidak boleh kosong")
      return
    }

    if (isReadOnly) {
      void message.error("Hanya approver atau PIC yang dapat mengubah data ini")
      return
    }

    try {
      setIsSubmitting(true)
      await updateSapSupplier(recordId, {
        comment,
        payload,
      })
      removeSupplierEditDraft(recordId)
      setInitialSnapshot(JSON.stringify({ payload }))
      setIsCommentModalVisible(false)
      commentForm.resetFields()
      void message.success("Supplier updated successfully")
      navigate(`/sapspl/view/${recordId}`)
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || "Failed to update supplier"
          : "Failed to update supplier"
      void message.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCommentModal = () => {
    if (isReadOnly || !isDirty) return
    setIsCommentModalVisible(true)
  }

  const handleCommentConfirm = async () => {
    try {
      await commentForm.validateFields(["comment"])
      const rawCommentValue: unknown = commentForm.getFieldValue("comment")

      if (typeof rawCommentValue !== "string") {
        return undefined
      }

      await handleSubmit(rawCommentValue)
    } catch (_error) {
      return undefined
    }

    return undefined
  }

  if (isLoading && !currentSupplier) {
    return (
      <TempLayout title="SAP - Edit Supplier" navNavigateUrl="/sapspl/history">
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Spin />
        </div>
      </TempLayout>
    )
  }

  return (
    <TempLayout title="SAP - Edit Supplier" navNavigateUrl="/sapspl/history">
      {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      {!currentSupplier ? (
        <Alert message="Not Found" description="Supplier not found or ID missing" type="warning" showIcon />
      ) : (
        <Card>
          {isApproved && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              message="Approved supplier cannot be edited"
              description="Data sudah APPROVED. Halaman ini ditampilkan read-only."
            />
          )}
          {!isApproved && !editAccess.canEdit && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
              message="Read-only access"
              description="Hanya approver atau PIC yang dapat mengedit data ini."
            />
          )}

          <div style={{ width: "100%", maxWidth: "none" }}>
            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <UserOutlined /> User Information
                  </>
                }
                key="1"
              >
                <Descriptions column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Employee ID">{currentSupplier.employee_id}</Descriptions.Item>
                  <Descriptions.Item label="Fullname">{currentSupplier.fullname}</Descriptions.Item>
                  <Descriptions.Item label="Level">{currentSupplier.level || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Department">{currentSupplier.department || "-"}</Descriptions.Item>
                </Descriptions>
              </Collapse.Panel>
            </Collapse>

            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <FileTextOutlined /> Request Details
                  </>
                }
                key="1"
              >
                <Descriptions column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="Type of Request">{currentSupplier.type_of_request || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Approval Status">{currentSupplier.approval_status || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Note">{currentSupplier.note || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Main File">{currentSupplier.file_name || "-"}</Descriptions.Item>
                </Descriptions>
              </Collapse.Panel>
            </Collapse>

            {!!currentSupplier.attachments?.length && (
              <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
                <Collapse.Panel
                  header={
                    <>
                      <PaperClipOutlined /> Attachments
                    </>
                  }
                  key="1"
                >
                  <Descriptions column={1}>
                    {currentSupplier.attachments.map((attachment, index) => (
                      <Descriptions.Item key={`${attachment.file_key || index}`} label={`Attachment ${index + 1}`}>
                        {attachment.file_name || "-"}{attachment.description ? ` - ${attachment.description}` : ""}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Collapse.Panel>
              </Collapse>
            )}

            <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
              <Collapse.Panel
                header={
                  <>
                    <TableOutlined /> Supplier Data
                  </>
                }
                key="1"
              >
                <SupplierPayloadEditor columns={columns} items={payload} disabled={isReadOnly} onChange={setPayload} />
              </Collapse.Panel>
            </Collapse>

            {!isReadOnly && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16, borderWidth: 1, borderStyle: "solid" }}
                message="Review changes carefully before saving"
                description="Once the approval process is complete or the record is APPROVED, this supplier can no longer be edited in the application."
              />
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 8 }}>
              <Button onClick={handleCancel}>Cancel</Button>
              {!isReadOnly && (
                <Button type="primary" onClick={openCommentModal} loading={isSubmitting} disabled={!isDirty || isSubmitting} style={{ minWidth: 140 }}>
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Modal
        title="Komentar Update"
        open={isCommentModalVisible}
        okText="Submit Update"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        onCancel={() => {
          if (isSubmitting) return
          setIsCommentModalVisible(false)
          commentForm.resetFields()
        }}
        onOk={() => {
          void handleCommentConfirm()
        }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Final confirmation"
          description="Make sure the supplier changes are correct. After the record is fully approved, editing will no longer be available in the application."
        />
        <Form form={commentForm} layout="vertical">
          <Form.Item
            label="Komentar"
            name="comment"
            rules={[
              { required: true, message: "Komentar wajib diisi" },
              { whitespace: true, message: "Komentar wajib diisi" },
            ]}
          >
            <Input.TextArea rows={4} placeholder="Jelaskan perubahan yang dilakukan" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </TempLayout>
  )
}

export default SapSuppliersEdit
